import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, Select, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'

type LegalDoc = {
  id: string
  slug: string
  title: string
  status: string
  contactEmail: string | null
  latestVersion: { version: string; status: string; publishedAt: string | null } | null
}

export function PagesLegales() {
  const toast = useToast()
  const qc = useQueryClient()
  const [edit, setEdit] = useState<LegalDoc | null>(null)
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState('DRAFT')

  const list = useQuery({
    queryKey: ['admin-legal'],
    queryFn: () => api<{ items: LegalDoc[] }>('/admin/legal'),
  })

  const patch = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/legal/${edit!.slug}`, 'PATCH', { title, status }),
    onSuccess: () => {
      toast.ok('Page légale mise à jour')
      setEdit(null)
      void qc.invalidateQueries({ queryKey: ['admin-legal'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucune page légale" />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Slug', 'Titre', 'Statut', 'Version', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 font-medium">{d.slug}</td>
                  <td className="px-4 py-2.5">{d.title}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill
                      label={d.status}
                      tone={
                        d.status === 'PUBLISHED'
                          ? 'ok'
                          : d.status === 'ARCHIVED'
                            ? 'neutral'
                            : 'warn'
                      }
                    />
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">
                    {d.latestVersion?.version ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <RowActions
                      items={[
                        {
                          label: 'Modifier',
                          onSelect: () => {
                            setEdit(d)
                            setTitle(d.title)
                            setStatus(d.status)
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={Boolean(edit)}
        title={edit ? `Éditer · ${edit.slug}` : 'Page légale'}
        onClose={() => setEdit(null)}
        footer={
          <>
            <BtnGhost onClick={() => setEdit(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={patch.isPending} onClick={() => patch.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Titre">
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label="Statut">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {['DRAFT', 'PUBLISHED', 'ARCHIVED'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Modal>
    </div>
  )
}
